import type {
  ContactChannel,
  ContractContactAlertType,
  ContractContactResource,
  OccupantContactMethod,
} from "../types";
import type { OccupantFormValues } from "../hooks/use-occupant-form-schema";

import { destinationKey, destinationKindOf, normalizeDestination } from "./destination";
import { phoneDigits } from "@/utils/phone";

/**
 * Maps the drawer onto what the API accepts, in two separate writes:
 *
 * - the person — `POST /folloportal/contacts` to create, `PATCH …/{id}` to edit —
 *   with a LIST of phone numbers and a LIST of email addresses (each list, when
 *   sent, replaces every destination of that kind; the first one is primary);
 * - the alerts — one `contract-contacts` row per (destination, alert type),
 *   naming the destination by its value.
 */

/** One destination as the contact endpoints take it. */
export interface ContactDestinationInput {
  value: string;
  /** Only sent when there is one: the backend stores a blank label as none. */
  label?: string;
}

/** The `attributes` of a contact write. `is_primary` is deliberately absent —
 * the portal may not set it, so it is never sent. */
export interface ContactAttributesPayload {
  first_name: string;
  last_name: string;
  phones: ContactDestinationInput[];
  emails: ContactDestinationInput[];
}

/** The rows the user filled in, as destinations; blank rows are dropped. */
const toDestinations = (rows: { label: string; value: string }[]): ContactDestinationInput[] =>
  rows
    .filter((row) => row.value !== "")
    .map((row) => (row.label !== "" ? { value: row.value, label: row.label } : { value: row.value }));

export const contactAttributes = (values: OccupantFormValues): ContactAttributesPayload => ({
  first_name: values.first_name,
  last_name: values.last_name,
  phones: toDestinations(values.phones),
  emails: toDestinations(values.emails),
});

/** One subscription the contract should hold: an alert type on a destination. */
export interface OccupantSubscription {
  alertType: ContractContactAlertType;
  destination: string;
}

/**
 * Identifies a subscription: its alert type plus its destination, normalized the
 * way the backend stores it — so `514-300-5432` typed in the drawer and
 * `5143005432` served back are the same subscription, not a removal plus a
 * re-creation (which would also reset the opt-in).
 */
export const subscriptionKey = (alertType: ContractContactAlertType, destination: string | null | undefined) =>
  `${alertType}|${normalizeDestination(destinationKindOf(alertType), destination)}`;

/**
 * Flattens the drawer's destinations into the subscriptions they imply — one
 * per (destination, alert type). Two numbers both ticked for SMS give two SMS
 * rows; the same number listed twice gives one.
 */
export const occupantSubscriptions = (values: OccupantFormValues): OccupantSubscription[] => {
  const phones = values.phones
    .filter((phone) => phone.value !== "")
    .flatMap((phone) => [
      ...(phone.alert_sms ? [{ alertType: "sms" as const, destination: phone.value }] : []),
      ...(phone.alert_call ? [{ alertType: "call" as const, destination: phone.value }] : []),
    ]);
  const emails = values.emails
    .filter((email) => email.value !== "" && email.alert_email)
    .map((email) => ({ alertType: "email" as const, destination: email.value }));

  const byKey = new Map<string, OccupantSubscription>();

  for (const subscription of [...phones, ...emails]) {
    const key = subscriptionKey(subscription.alertType, subscription.destination);

    if (!byKey.has(key)) {
      byKey.set(key, subscription);
    }
  }

  return [...byKey.values()];
};

/** What a save has to write for the alerts to match the drawer. */
export interface SubscriptionPlan<TSubscription extends OccupantSubscription = OccupantSubscription> {
  /** Subscriptions to store — ticked, and not on the contract yet. */
  additions: TSubscription[];
  /** Ids of the rows to destroy — on the contract, and no longer ticked. */
  removals: string[];
}

/**
 * Reconciles the contact's subscriptions on this contract with what the drawer
 * asks for, keyed by (alert type, normalized destination). A row that is still
 * wanted is left alone so its opt-in survives the edit; a row that isn't — an
 * unticked box, a number that was corrected or removed, a row with nowhere to
 * deliver — is destroyed, and a wanted one with no row yet is stored.
 *
 * Moving an alert to another number is therefore a destroy plus a store: the
 * new number has not been opted in, so starting its consent over is right.
 */
export const subscriptionPlan = <TSubscription extends OccupantSubscription>(
  existing: ContractContactResource[],
  wanted: TSubscription[],
): SubscriptionPlan<TSubscription> => {
  const wantedByKey = new Map(
    wanted.map((subscription) => [subscriptionKey(subscription.alertType, subscription.destination), subscription]),
  );
  const kept = new Set<string>();
  const removals: string[] = [];

  for (const contractContact of existing) {
    const { alert_type: alertType, alert_destination: destination } = contractContact.attributes;
    const key = alertType && destination ? subscriptionKey(alertType, destination) : null;

    // The second of two rows delivering the same alert to the same place is a
    // duplicate: one is kept, the other goes.
    if (key !== null && wantedByKey.has(key) && !kept.has(key)) {
      kept.add(key);
      continue;
    }

    removals.push(String(contractContact.id));
  }

  const additions = [...wantedByKey].filter(([key]) => !kept.has(key)).map(([, subscription]) => subscription);

  return { additions, removals };
};

/** A destination list reduced to what the contact endpoints store: the
 * normalized value and the label, in order (the first one is primary). */
const storedDestinations = (rows: { label: string; value: string }[], kind: "phone" | "email") =>
  rows
    .filter((row) => row.value.trim() !== "")
    .map((row) => `${normalizeDestination(kind, row.value)}|${row.label.trim()}`)
    .join("\n");

/**
 * What an edit changed on the person, as a partial contact write: each name on
 * its own, and a destination list whole (the endpoint replaces every destination
 * of a kind it is sent) when one of its values, labels or its order changed.
 * Only the alert boxes changing gives `{}` — the contact write is then skipped:
 * it would be a no-op, and it is refused outright for a contact that is on the
 * contract without being the customer's own, which would block ticking a box.
 *
 * Sending only what changed also keeps an untouched name untouched: a migrated
 * contact holds its name in `fullname`, which blank halves must not overwrite.
 */
export const contactChanges = (
  before: OccupantFormValues,
  after: OccupantFormValues,
): Partial<ContactAttributesPayload> => {
  const attributes = contactAttributes(after);

  return {
    ...(before.first_name.trim() !== after.first_name.trim() && { first_name: attributes.first_name }),
    ...(before.last_name.trim() !== after.last_name.trim() && { last_name: attributes.last_name }),
    ...(storedDestinations(before.phones, "phone") !== storedDestinations(after.phones, "phone") && {
      phones: attributes.phones,
    }),
    ...(storedDestinations(before.emails, "email") !== storedDestinations(after.emails, "email") && {
      emails: attributes.emails,
    }),
  };
};

/**
 * Every way a contact can be reached, as the API serves it: `channels` — the
 * record the backend owns — then the `phones` / `emails` arrays, then the legacy
 * single-value columns for a contact served the old way.
 */
export interface ContactMethodAttributes {
  /** The destinations as the backend owns them, ids included. */
  channels?: ContactChannel[] | null;
  phones?: OccupantContactMethod[] | null;
  emails?: OccupantContactMethod[] | null;
  phone?: string | null;
  phone2?: string | null;
  phone3?: string | null;
  email?: string | null;
}

/**
 * Which alert types each destination receives on this contract, keyed by
 * {@link destinationKey}. Read off the rows' `alert_destination` — where the
 * backend says the row delivers — normalized, so a legacy contact whose columns
 * hold `514-300-5432` still lines up with a row delivering to `5143005432`.
 *
 * The copies of the alerts nested on the contact are deliberately not read: they
 * span every contract the contact is on, and a tick borrowed from another
 * contract would be saved onto this one.
 */
export const deliveredAlertTypes = (contractContacts: ContractContactResource[]) => {
  const delivered = new Map<string, Set<ContractContactAlertType>>();

  for (const contractContact of contractContacts) {
    const { alert_type: alertType, alert_destination: destination } = contractContact.attributes;

    if (!alertType || !destination) {
      continue;
    }

    const key = destinationKey(destinationKindOf(alertType), destination);
    const alertTypes = delivered.get(key) ?? new Set<ContractContactAlertType>();

    alertTypes.add(alertType);
    delivered.set(key, alertTypes);
  }

  return delivered;
};

/** A destination as the drawer lists it, before the alert boxes are resolved. */
interface DestinationEntry {
  label: string;
  value: string;
}

/**
 * Every destination on file, in the order the drawer should list them: the
 * primary of each kind first, then by position — the backend's own order. It
 * matters beyond looks: a list sent back makes its FIRST entry primary, so an
 * edit that only relabels a number must not hand the flag to another one.
 */
const destinationEntries = (contact: ContactMethodAttributes | undefined) => {
  if (contact?.channels?.length) {
    const sorted = [...contact.channels].sort(
      (a, b) => Number(Boolean(b.is_primary)) - Number(Boolean(a.is_primary)) || (a.position ?? 0) - (b.position ?? 0),
    );
    const entries = (type: ContactChannel["type"]): DestinationEntry[] =>
      sorted
        .filter((channel) => channel.type === type)
        .map((channel) => ({ label: channel.label ?? "", value: channel.value }));

    return { phones: entries("phone"), emails: entries("email") };
  }

  const fromMethods = (methods: OccupantContactMethod[] | null | undefined): DestinationEntry[] | null =>
    methods ? methods.map((method) => ({ label: method.label ?? "", value: method.value })) : null;

  return {
    phones:
      fromMethods(contact?.phones) ??
      [contact?.phone, contact?.phone3, contact?.phone2]
        .filter((value): value is string => Boolean(value))
        // The legacy columns carried no label of their own.
        .map((value) => ({ label: "", value })),
    emails: fromMethods(contact?.emails) ?? (contact?.email ? [{ label: "", value: contact.email }] : []),
  };
};

/**
 * Turns a contact's destinations into editable form rows, with each row's alert
 * boxes ticked from the subscriptions that deliver to it. Pass no rows for a
 * contact that has no subscription on this contract yet, so every box starts
 * unticked. Values are returned as stored; display formatting is the caller's.
 */
export const contactMethodRows = (
  contact: ContactMethodAttributes | undefined,
  contractContacts: ContractContactResource[] = [],
) => {
  const delivered = deliveredAlertTypes(contractContacts);
  const { phones: phoneEntries, emails: emailEntries } = destinationEntries(contact);
  const ticksOf = (kind: "phone" | "email", value: string) =>
    delivered.get(destinationKey(kind, value)) ?? new Set<ContractContactAlertType>();

  const phones = phoneEntries
    .filter((entry) => entry.value !== "")
    .map((entry) => {
      const alertTypes = ticksOf("phone", entry.value);

      return {
        label: entry.label,
        value: entry.value,
        alert_call: alertTypes.has("call"),
        alert_sms: alertTypes.has("sms"),
      };
    });

  const emails = emailEntries
    .filter((entry) => entry.value !== "")
    .map((entry) => ({
      label: entry.label,
      value: entry.value,
      alert_email: ticksOf("email", entry.value).has("email"),
    }));

  return {
    // A blank row so there is always somewhere to type.
    phones: phones.length > 0 ? phones : [{ label: "", value: "", alert_call: false, alert_sms: false }],
    emails: emails.length > 0 ? emails : [{ label: "", value: "", alert_email: false }],
  };
};

/**
 * The whole drawer state for an existing occupant, read from the rows the drawer
 * fetched: the person, their destinations and the alert types each one carries.
 * Numbers are held as digits — the phone input shows them dashed — and the save
 * compares them normalized, so an untouched number is never mistaken for a new one. Nothing is lifted from
 * the table, so what is edited is what the server currently holds.
 */
export const occupantFormValuesFrom = (contractContacts: ContractContactResource[]): Partial<OccupantFormValues> => {
  const contact = contractContacts[0]?.relationships?.contact?.data;
  const { phones, emails } = contactMethodRows(contact?.attributes, contractContacts);

  return {
    contactId: String(contact?.id ?? ""),
    first_name: contact?.attributes?.first_name ?? "",
    last_name: contact?.attributes?.last_name ?? "",
    phones: phones.map((phone) => ({ ...phone, value: phoneDigits(phone.value) })),
    emails,
  };
};

/** The occupant's display name, for the drawer header: `name` — whichever shape
 * the contact holds its name in, so a migrated contact is not nameless — then the
 * two halves. The "NULL" placeholder older dialogs wrote is dropped, as the list
 * does. */
export const occupantName = (contractContacts: ContractContactResource[]) => {
  const contact = contractContacts[0]?.relationships?.contact?.data?.attributes;
  const name = contact?.name ?? [contact?.first_name, contact?.last_name].filter(Boolean).join(" ");

  return name
    .split(/\s+/)
    .filter((part) => part !== "" && part !== "NULL")
    .join(" ");
};
